import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
  Badge,
  IconButton,
  Button,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  People,
  Inventory,
  ShoppingCart,
  AttachMoney,
  LocalOffer,
  Campaign,
  Store,
  Category,
  Pending,
  VerifiedUser,
  SupervisorAccount,
  PlayArrow,
  Storefront,
  RateReview,
  Settings,
  Email,
  TrendingUp,
  Notifications,
  LocalShipping,
  Assignment,
  CheckCircle,
  Cancel,
  Visibility,
  Speed,
  NewReleases,
  ContactPhone,
  Refresh,
  FilterList,
  MoreVert,
  Star,
  Warning,
  Info,
  Search,
  FilterAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getDashboardStats } from '../store/slices/dashboardSlice';
import { getOrders } from '../store/slices/orderSlice';
import { getUsers } from '../store/slices/userSlice';
import { getProducts } from '../store/slices/productSlice';
import { getCategories } from '../store/slices/categorySlice';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{
    height: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 3,
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      transform: 'translateY(-4px)',
      '& .icon-bg': {
        transform: 'scale(1.1)',
        opacity: 0.2
      }
    },
  }}>
    <Box className="icon-bg" sx={{
      position: 'absolute',
      right: -20,
      top: -20,
      color: color,
      opacity: 0.1,
      transform: 'rotate(15deg)',
      transition: 'all 0.3s ease',
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 120 } })}
    </Box>
    <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography
            sx={{
              color: '#666666',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: '#1a1a1a',
              fontWeight: 800,
              mb: 0.5
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#888', fontWeight: 500 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}15`, // 15% opacity hex
            borderRadius: '16px',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SectionHeader = ({ title, icon }) => (
  <Box display="flex" alignItems="center" sx={{ mb: 2, mt: 4 }}>
    <Box sx={{ color: '#ff2852', mr: 1, display: 'flex' }}>
      {icon}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
      {title}
    </Typography>
  </Box>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Selectors
  const { stats, isLoading: statsLoading } = useSelector((state) => state.dashboard);
  const { orders, isLoading: ordersLoading } = useSelector((state) => state.orders);
  const { users, isLoading: usersLoading } = useSelector((state) => state.users);
  const { products, isLoading: productsLoading } = useSelector((state) => state.products);
  const { categories, isLoading: categoriesLoading } = useSelector((state) => state.categories);
  const navigate = useNavigate();

  // Enhanced Products Tab State
  const [productFilter, setProductFilter] = useState('all'); // 'all' or 'active'
  const [productPage, setProductPage] = useState(0);
  const [productRowsPerPage, setProductRowsPerPage] = useState(10);
  const [productSearch, setProductSearch] = useState('');
  const [isProductsRefreshing, setIsProductsRefreshing] = useState(false);

  // State for notifications and tabs
  const [newOrderAlert, setNewOrderAlert] = React.useState(null);
  const [lastOrderCount, setLastOrderCount] = React.useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Initial Fetch
  useEffect(() => {
    dispatch(getDashboardStats());
    dispatch(getOrders());
    dispatch(getUsers());
    dispatch(getProducts({ limit: 1000 })); // Fetch enough products for stats
    dispatch(getCategories());

    const interval = setInterval(() => {
      dispatch(getDashboardStats()); // Keep legacy stats for backup/revenue
      dispatch(getOrders());
    }, 10000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Generate notifications based on data
  useEffect(() => {
    const newNotifications = [];
    
    // Seller verification notifications
    const pendingSellers = users?.filter(user => 
      Array.isArray(user.role) ? user.role.includes('seller') : user.role === 'seller'
    ).filter(seller => seller.sellerStatus === 'pending') || [];
    
    pendingSellers.slice(0, 3).forEach(seller => {
      newNotifications.push({
        id: `seller-${seller._id}`,
        type: 'seller_verification',
        title: 'Seller Verification Request',
        message: `${seller.brandName || seller.name} wants to become a verified seller`,
        time: new Date(seller.createdAt).toLocaleDateString(),
        priority: 'high',
        action: () => navigate('/sellers'),
        icon: <Store />,
        color: '#ff2852'
      });
    });

    // Product approval notifications
    const pendingProducts = products?.filter(product => product.verificationStatus === 'pending') || [];
    
    if (pendingProducts.length > 0) {
      newNotifications.push({
        id: 'products-pending',
        type: 'product_approval',
        title: 'Products Pending Approval',
        message: `${pendingProducts.length} products waiting for your approval`,
        time: 'Just now',
        priority: 'medium',
        action: () => navigate('/products/pending'),
        icon: <Inventory />,
        color: '#f59e0b'
      });
    }

    // New order notifications
    if (orders && orders.length > lastOrderCount && lastOrderCount > 0) {
      const newOrders = orders.slice(0, orders.length - lastOrderCount);
      newOrders.slice(0, 2).forEach(order => {
        newNotifications.push({
          id: `order-${order._id}`,
          type: 'new_order',
          title: 'New Order Received',
          message: `Order #${order.orderNumber} from ${order.user?.name || 'Guest'}`,
          time: new Date(order.createdAt).toLocaleDateString(),
          priority: 'high',
          action: () => navigate(`/orders/${order._id}`),
          icon: <ShoppingCart />,
          color: '#10b981'
        });
      });
    }

    // Delivery notifications
    const outForDeliveryOrders = orders?.filter(order => 
      order.status === 'out-for-delivery' || order.orderStatus === 'out-for-delivery'
    ) || [];
    
    if (outForDeliveryOrders.length > 0) {
      newNotifications.push({
        id: 'delivery-pending',
        type: 'delivery',
        title: 'Orders Out for Delivery',
        message: `${outForDeliveryOrders.length} orders are currently out for delivery`,
        time: 'Today',
        priority: 'medium',
        action: () => navigate('/orders'),
        icon: <LocalShipping />,
        color: '#3b82f6'
      });
    }

    setNotifications(newNotifications);
  }, [users, products, orders, lastOrderCount, navigate]);

  // Alert logic
  useEffect(() => {
    if (orders && orders.length > lastOrderCount && lastOrderCount > 0) {
      const newOrders = orders.slice(0, orders.length - lastOrderCount);
      if (newOrders.length > 0) {
        setNewOrderAlert({
          message: `New order received! ${newOrders.length} ${newOrders.length === 1 ? 'order' : 'orders'} placed.`,
          order: newOrders[0],
        });
      }
    }
    if (orders) {
      setLastOrderCount(orders.length);
    }
  }, [orders, lastOrderCount]);

  const handleCloseAlert = () => setNewOrderAlert(null);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(getDashboardStats()),
        dispatch(getOrders()),
        dispatch(getUsers()),
        dispatch(getProducts({ limit: 1000 })),
        dispatch(getCategories())
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Refresh products only (for products tab)
  const handleRefreshProducts = async () => {
    setIsProductsRefreshing(true);
    try {
      await dispatch(getProducts({ limit: 1000 }));
    } catch (error) {
      console.error('Error refreshing products:', error);
    } finally {
      setIsProductsRefreshing(false);
    }
  };

  // Filter products based on filter and search
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    
    let filtered = products;
    
    // Apply active/inactive filter
    if (productFilter === 'active') {
    }
    
    // Apply search filter
    if (productSearch) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        (product.brand && product.brand.toLowerCase().includes(productSearch.toLowerCase())) ||
        (product.category?.name && product.category.name.toLowerCase().includes(productSearch.toLowerCase()))
      );
    }
    
    return filtered;
  }, [products, productFilter, productSearch]);

  // Pagination for products
  const paginatedProducts = useMemo(() => {
    const startIndex = productPage * productRowsPerPage;
    const endIndex = startIndex + productRowsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, productPage, productRowsPerPage]);

  const handleProductPageChange = (event, newPage) => {
    setProductPage(newPage);
  };

  const handleProductRowsPerPageChange = (event) => {
    setProductRowsPerPage(parseInt(event.target.value, 10));
    setProductPage(0);
  };

  // --- Client-Side Stats Calculation ---
  const calculatedStats = useMemo(() => {
    // 1. User Stats
    let adminsCount = 0;
    let sellersCount = 0;
    let buyersCount = 0;
    const sellersList = [];
    const recentSellersList = [];
    let freeSellersCount = 0;
    let basicSellersCount = 0;
    let growthSellersCount = 0;
    let businessSellersCount = 0;

    if (users && users.length > 0) {
      users.forEach(user => {
        const role = user.role;
        const roles = Array.isArray(role) ? role : [role];

        // Strict Priority: Admin > Seller > Buyer
        if (roles.includes('admin')) {
          adminsCount++;
        } else if (roles.includes('seller')) {
          sellersCount++;
          sellersList.push(user);
          // Check for recent sellers (last 30 days? or just sort by createdAt later)
          recentSellersList.push(user);
          // Count plan-wise sellers
          const plan = user.plan || 'free';
          if (plan === 'free') freeSellersCount++;
          else if (plan === 'basic') basicSellersCount++;
          else if (plan === 'growth') growthSellersCount++;
          else if (plan === 'business') businessSellersCount++;
        } else {
          buyersCount++;
        }
      });
    }

    // Sort recent sellers
    recentSellersList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // 2. Product Stats
    let pendingProducts = 0;
    let activeProducts = 0;
    const topProductsList = [];

    if (products && products.length > 0) {
      products.forEach(product => {
        if (product.verificationStatus === 'pending') pendingProducts++;
        if (product.isActive) activeProducts++;
      });
      // Sort for top products (using price or stock as proxy if soldCount unavailable on frontend)
      topProductsList.push(...products.filter(p => p.isActive));
      topProductsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Newest first fallback
    }

    // 3. Category Stats
    let rootCategories = 0;
    let subCategories = 0;

    if (categories && categories.length > 0) {
      categories.forEach(cat => {
        if (cat.parent || cat.parentCategory) {
          subCategories++;
        } else {
          rootCategories++;
        }
      });
    }

    // 4. Order Revenue (Fallback if stats API fails)
    let totalRevenue = 0;
    const sellerRevenueMap = {};

    if (orders) {
      orders.forEach(order => {
        totalRevenue += (order.totalAmount || 0);

        // Calculate top sellers from orders
        if (order.items) {
          order.items.forEach(item => {
            if (item.seller && item.seller._id) {
              const sid = item.seller._id;
              if (!sellerRevenueMap[sid]) {
                sellerRevenueMap[sid] = {
                  revenue: 0,
                  count: 0,
                  seller: item.seller
                };
              }
              // Handle potential undefined price/quantity
              const itemTotal = (item.price || 0) * (item.quantity || 1);
              sellerRevenueMap[sid].revenue += itemTotal;
              sellerRevenueMap[sid].count += 1;
            }
          });
        }
      });
    }

    // Convert map to sorted array
    const topSellersCalculated = Object.values(sellerRevenueMap)
      .sort((a, b) => b.revenue - a.revenue)
      .map(s => ({
        ...s.seller,
        totalRevenue: s.revenue,
        totalOrders: s.count
      }))
      .slice(0, 5);


    return {
      adminsCount,
      sellersCount,
      buyersCount,
      totalUsers: users?.length || 0,
      totalOrders: orders?.length || 0,
      completedOrders: orders?.filter(order => order.status === 'delivered' || order.orderStatus === 'delivered').length || 0,
      pendingProducts,
      activeProducts,
      totalProducts: products?.length || 0,
      rootCategories,
      subCategories,
      recentSellers: recentSellersList.slice(0, 5),
      topProducts: topProductsList.slice(0, 5),
      topSellers: topSellersCalculated,
      totalRevenue,
      sellerPlanStats: {
        free: freeSellersCount,
        basic: basicSellersCount,
        growth: growthSellersCount,
        business: businessSellersCount
      }
    };
  }, [users, products, categories, orders]);

  const isLoading = statsLoading || usersLoading || productsLoading || categoriesLoading;

  // Use calculated revenue if backend stats are empty/zero, otherwise prefer backend if available
  const displayRevenue = stats?.totalRevenue || calculatedStats.totalRevenue || 0;

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header with Refresh - Mobile Optimized */}
      {/* <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={{ xs: 2, sm: 3 }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            color: '#1a1a1a',
            fontWeight: 800,
            background: 'linear-gradient(45deg, #ff2852 30%, #ff4d4d 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Dashboard Overview
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{
                bgcolor: 'white',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <Refresh sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
          <Badge badgeContent={notifications.length} color="error">
            <IconButton 
              onClick={() => setActiveTab(3)}
              sx={{
                bgcolor: 'white',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <Notifications />
            </IconButton>
          </Badge>
        </Box>
      </Box> */}

      {/* Progress Bar for Loading */}
      {refreshing && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      {/* Navigation Tabs - Single Row Layout */}
      <Paper sx={{ mb: 3, borderRadius: 2, p: 2 }}>
        <Box display="flex" gap={1} flexWrap="nowrap" justifyContent="space-between">
          {[
            { id: 0, icon: <Speed />, label: "Overview", color: "#ff2852" },
            { id: 1, icon: <Store />, label: "Sellers", color: "#10b981" },
            { id: 2, icon: <Inventory />, label: "Products", color: "#f59e0b" },
            { id: 3, icon: <Notifications />, label: "Notifications", color: "#3b82f6" },
            { id: 4, icon: <LocalShipping />, label: "Delivery", color: "#8b5cf6" }
          ].map((tab) => (
            <Box
              key={tab.id}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                maxWidth: 'none'
              }}
            >
              <Paper
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: activeTab === tab.id ? `2px solid ${tab.color}` : '1px solid #e0e0e0',
                  backgroundColor: activeTab === tab.id ? `${tab.color}10` : '#ffffff',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  minHeight: { xs: 60, sm: 70 },
                  '&:hover': {
                    backgroundColor: `${tab.color}15`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ color: activeTab === tab.id ? tab.color : '#666', mb: 1, fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
                  {tab.icon}
                </Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{ 
                    color: activeTab === tab.id ? tab.color : '#666',
                    fontSize: { xs: '0.7rem', sm: '0.9rem' },
                    lineHeight: 1.2
                  }}
                >
                  {tab.label}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Paper>

      <Snackbar
        open={!!newOrderAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          {newOrderAlert?.message}
        </Alert>
      </Snackbar>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Mobile Quick Action Grid */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 4 }}>
            <Grid container spacing={2}>
              {[
                { title: 'Products', icon: <Inventory />, path: '/products', color: '#0984e3' },
                { title: 'Trending', icon: <TrendingUp />, path: '/products/trending', color: '#e84393' },
                { title: 'Videos', icon: <PlayArrow />, path: '/watch-videos', color: '#d63031' },
                { title: 'Sellers', icon: <Storefront />, path: '/sellers', color: '#6c5ce7' },
                { 
                  title: 'Pending', 
                  icon: (
                    <Badge 
                      badgeContent={calculatedStats.pendingProducts} 
                      color="error" 
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.6rem',
                          height: 16,
                          minWidth: 16,
                        }
                      }}
                    >
                      <Pending />
                    </Badge>
                  ), 
                  path: '/pending-products', 
                  color: '#ff2852' 
                },
                { title: 'Users', icon: <People />, path: '/users', color: '#fab1a0' },
                { title: 'Orders', icon: <ShoppingCart />, path: '/orders', color: '#e17055' },
                { title: 'Reviews', icon: <RateReview />, path: '/reviews', color: '#fdcb6e' },
              ].map((item) => (
                <Grid item xs={3} key={item.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: item.color,
                      }
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <Box sx={{ color: item.color, mb: 1, fontSize: '1.5rem' }}>
                      {item.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#666' }}>
                      {item.title}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Stats Cards - Mobile Optimized */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={6} sm={6} md={6}>
              <StatCard
                title="Total Revenue"
                value={`PKR ${Math.round(displayRevenue).toLocaleString()}`}
                icon={<AttachMoney />}
                color="#ff2852"
                subtitle="Last 30 days"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <StatCard
                title="Total Orders"
                value={calculatedStats.totalOrders}
                icon={<ShoppingCart />}
                color="#3b82f6"
                subtitle={`${calculatedStats.completedOrders} completed`}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <StatCard
                title="Active Sellers"
                value={calculatedStats.sellersCount}
                icon={<Store />}
                color="#10b981"
                subtitle="Verified sellers"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <StatCard
                title="Active Products"
                value={calculatedStats.activeProducts}
                icon={<Inventory />}
                color="#f59e0b"
                subtitle={`${calculatedStats.totalProducts} total products`}
              />
            </Grid>
          </Grid>

          {/* Secondary Stats Row - Mobile Optimized */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', borderRadius: 3, border: '1px solid #eee' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <SupervisorAccount sx={{ color: '#ff2852', mr: 1 }} />
                  <Typography variant={isMobile ? "h6" : "h6"} fontWeight={700}>User Breakdown</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box textAlign="center" p={1} bgcolor="#fff5f5" borderRadius={2}>
                      <Typography variant="h5" color="#ff2852" fontWeight={700}>{calculatedStats.sellersCount}</Typography>
                      <Typography variant="caption" color="text.secondary">Sellers</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center" p={1} bgcolor="#f0f9ff" borderRadius={2}>
                      <Typography variant="h5" color="#0ea5e9" fontWeight={700}>{calculatedStats.buyersCount}</Typography>
                      <Typography variant="caption" color="text.secondary">Buyers</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center" p={1} bgcolor="#fdf4ff" borderRadius={2}>
                      <Typography variant="h5" color="#c026d3" fontWeight={700}>{calculatedStats.adminsCount}</Typography>
                      <Typography variant="caption" color="text.secondary">Admins</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', borderRadius: 3, border: '1px solid #eee' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Store sx={{ color: '#10b981', mr: 1 }} />
                  <Typography variant={isMobile ? "h6" : "h6"} fontWeight={700}>Seller Plan Breakdown</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="#e0f2fe" borderRadius={2}>
                      <Typography variant="h5" color="#0284c7" fontWeight={700}>
                        {stats?.sellerPlanStats?.free ?? calculatedStats.sellerPlanStats.free}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Free</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="#fef3c7" borderRadius={2}>
                      <Typography variant="h5" color="#d97706" fontWeight={700}>
                        {stats?.sellerPlanStats?.basic ?? calculatedStats.sellerPlanStats.basic}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Basic</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="#d1fae5" borderRadius={2}>
                      <Typography variant="h5" color="#059669" fontWeight={700}>
                        {stats?.sellerPlanStats?.growth ?? calculatedStats.sellerPlanStats.growth}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Growth</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="#f3e8ff" borderRadius={2}>
                      <Typography variant="h5" color="#9333ea" fontWeight={700}>
                        {stats?.sellerPlanStats?.business ?? calculatedStats.sellerPlanStats.business}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Business</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, height: '100%', borderRadius: 3, border: '1px solid #eee' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Store sx={{ color: '#ff2852', mr: 1 }} />
                  <Typography variant="h6" fontWeight={700}>Inventory Status</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box p={1.5} borderRadius={2} border="1px solid #f0f0f0">
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Categories</Typography>
                        <Category fontSize="small" color="action" />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>{calculatedStats.rootCategories}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box p={1.5} borderRadius={2} border="1px solid #f0f0f0">
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Subcategories</Typography>
                        <Category fontSize="small" color="action" />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>{calculatedStats.subCategories}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={1.5} borderRadius={2} bgcolor="#fff8e1" display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <Pending sx={{ color: '#f59e0b', mr: 1, fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={600} color="#b45309">Pending Approval</Typography>
                      </Box>
                      <Chip label={calculatedStats.pendingProducts} size="small" sx={{ bgcolor: '#f59e0b', color: 'white', fontWeight: 700 }} />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', borderRadius: 3, border: '1px solid #eee' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Campaign sx={{ color: '#ff2852', mr: 1 }} />
                  <Typography variant="h6" fontWeight={700}>Active Campaigns</Typography>
                </Box>
                <List dense>
                  <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 1 }}>
                    <ListItemText primary="Active Promotions" primaryTypographyProps={{ fontWeight: 600 }} />
                    <Chip label={stats?.activePromotions || 0} size="small" color="error" variant="outlined" />
                  </ListItem>
                  <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <ListItemText primary="Active Sales" primaryTypographyProps={{ fontWeight: 600 }} />
                    <Chip label={stats?.activeSales || 0} size="small" color="secondary" variant="outlined" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Main Content Areas */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} lg={8}>
              <SectionHeader title="Recent Orders" icon={<ShoppingCart />} />
              <Paper sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                {orders?.slice(0, 5).map((order, index) => (
                  <Box key={order._id}>
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: '0.2s',
                        '&:hover': { bgcolor: '#fafafa' }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: '#ffebee', color: '#ff2852' }}>
                          <LocalOffer />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Order #{order.orderNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.user?.name || 'Guest'} • {new Date(order.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="subtitle2" fontWeight={700} color="#ff2852">
                          PKR {order.totalAmount?.toLocaleString() || 0}
                        </Typography>
                        <Chip
                          label={order.status || order.orderStatus}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            mt: 0.5,
                            textTransform: 'capitalize',
                            bgcolor: order.status === 'delivered' ? '#e8f5e9' : order.status === 'cancelled' ? '#ffebee' : '#fff3e0',
                            color: order.status === 'delivered' ? '#2e7d32' : order.status === 'cancelled' ? '#d32f2f' : '#ed6c02',
                          }}
                        />
                      </Box>
                    </Box>
                    {index < 4 && <Divider />}
                  </Box>
                ))}
                {(!orders || orders.length === 0) && (
                  <Box p={4} textAlign="center"><Typography color="text.secondary">No recent orders</Typography></Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SectionHeader title="Newest Sellers" icon={<VerifiedUser />} />
              <Paper sx={{ borderRadius: 3, border: '1px solid #e0e0e0', p: 0 }}>
                <List disablePadding>
                  {calculatedStats.recentSellers.map((seller, index) => (
                    <React.Fragment key={seller._id}>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar src={seller.profileImage}>{seller.brandName?.[0] || seller.name?.[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={seller.brandName || seller.name}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                          secondary={
                            <Box component="span" display="flex" flexDirection="column">
                              <Typography variant="caption" color="text.secondary">{seller.email}</Typography>
                              <Box mt={0.5}>
                                <Chip
                                  label={seller.sellerStatus || 'Pending'}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    textTransform: 'uppercase',
                                    bgcolor: seller.sellerStatus === 'verified' ? '#e8f5e9' : '#fff3e0',
                                    color: seller.sellerStatus === 'verified' ? '#2e7d32' : '#ed6c02',
                                  }}
                                />
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < calculatedStats.recentSellers.length - 1 && <Divider component="li" variant="inset" />}
                    </React.Fragment>
                  ))}
                  {calculatedStats.recentSellers.length === 0 && (
                    <Box p={2} textAlign="center"><Typography color="text.secondary" variant="caption">No sellers found</Typography></Box>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Sellers Tab - Enhanced */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionHeader title="Seller Management" icon={<Store />} />
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="body1" color="text.secondary" mb={2}>
                Manage seller verification requests, performance monitoring, and seller analytics.
              </Typography>
              
              {/* Quick Actions */}
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/sellers')}
                  sx={{ bgcolor: '#ff2852', '&:hover': { bgcolor: '#d63031' } }}
                >
                  All Sellers
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/sellers/pending')}
                  sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                >
                  Pending Approval ({users?.filter(u => u.sellerStatus === 'pending').length || 0})
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/sellers/verified')}
                  sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                >
                  Verified Sellers
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/sellers/performance')}
                  sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                >
                  Performance Analytics
                </Button>
              </Box>
              
              {/* Seller Stats */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ff285210', borderRadius: 2 }}>
                    <Store sx={{ fontSize: 32, color: '#ff2852', mb: 1 }} />
                    <Typography variant="h4" color="#ff2852" fontWeight={700}>
                      {users?.filter(u => u.role === 'seller' || (Array.isArray(u.role) && u.role.includes('seller'))).length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Sellers</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10b98115', borderRadius: 2 }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
                    <Typography variant="h4" color="#10b981" fontWeight={700}>
                      {users?.filter(u => u.sellerStatus === 'verified').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Verified Sellers</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f59e0b15', borderRadius: 2 }}>
                    <Pending sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
                    <Typography variant="h4" color="#f59e0b" fontWeight={700}>
                      {users?.filter(u => u.sellerStatus === 'pending').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Pending Approval</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#3b82f615', borderRadius: 2 }}>
                    <TrendingUp sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                    <Typography variant="h4" color="#3b82f6" fontWeight={700}>
                      {stats?.sellerPlanStats?.growth ?? calculatedStats.sellerPlanStats.growth}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Growth Plan</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Seller Plan Stats */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e0f2fe', borderRadius: 2 }}>
                    <Typography variant="h4" color="#0284c7" fontWeight={700}>
                      {stats?.sellerPlanStats?.free ?? calculatedStats.sellerPlanStats.free}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Free Plan</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fef3c7', borderRadius: 2 }}>
                    <Typography variant="h4" color="#d97706" fontWeight={700}>
                      {stats?.sellerPlanStats?.basic ?? calculatedStats.sellerPlanStats.basic}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Basic Plan</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#d1fae5', borderRadius: 2 }}>
                    <Typography variant="h4" color="#059669" fontWeight={700}>
                      {stats?.sellerPlanStats?.growth ?? calculatedStats.sellerPlanStats.growth}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Growth Plan</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e8ff', borderRadius: 2 }}>
                    <Typography variant="h4" color="#9333ea" fontWeight={700}>
                      {stats?.sellerPlanStats?.business ?? calculatedStats.sellerPlanStats.business}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Business Plan</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Products Tab - Enhanced */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionHeader title="Product Management" icon={<Inventory />} />
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="body1" color="text.secondary" mb={2}>
                Manage product approvals, inventory, and performance across all categories.
              </Typography>
              
              {/* Quick Stats */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ff285210', borderRadius: 2 }}>
                    <Typography variant="h4" color="#ff2852" fontWeight={700}>
                      {products?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Products
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10b98115', borderRadius: 2 }}>
                    <Typography variant="h4" color="#10b981" fontWeight={700}>
                      {products?.filter(p => p.isActive !== false).length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active Products
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f59e0b15', borderRadius: 2 }}>
                    <Typography variant="h4" color="#f59e0b" fontWeight={700}>
                      {products?.filter(p => p.verificationStatus === 'pending').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pending Approval
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#3b82f615', borderRadius: 2 }}>
                    <Typography variant="h4" color="#3b82f6" fontWeight={700}>
                      {products?.filter(p => (p.stock || 0) < 10).length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Low Stock
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Filter Controls - Mobile Optimized */}
              <Box 
                display="flex" 
                gap={{ xs: 1, sm: 2 }} 
                mb={3} 
                flexWrap="wrap" 
                alignItems="center"
                flexDirection={{ xs: 'column', sm: 'row' }}
              >
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: '100%', sm: 120 },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <InputLabel>Filter</InputLabel>
                  <Select
                    value={productFilter}
                    label="Filter"
                    onChange={(e) => setProductFilter(e.target.value)}
                    startAdornment={<FilterAlt sx={{ mr: 1, fontSize: 18 }} />}
                  >
                    <MenuItem value="all">All Products</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="pending">Pending Approval</MenuItem>
                    <MenuItem value="lowstock">Low Stock</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  size="small"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 250 },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                />
                
                <Box 
                  display="flex" 
                  gap={2} 
                  alignItems="center"
                  mt={{ xs: 2, sm: 0 }}
                  width={{ xs: '100%', sm: 'auto' }}
                >
                  <IconButton 
                    onClick={handleRefreshProducts}
                    disabled={isProductsRefreshing}
                    sx={{ 
                      border: '1px solid #ddd', 
                      borderRadius: 1,
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  >
                    <Refresh sx={{ 
                      animation: isProductsRefreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }} />
                  </IconButton>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      ml: { xs: 0, sm: 'auto' }
                    }}
                  >
                    Showing {paginatedProducts.length} of {filteredProducts.length} products
                  </Typography>
                </Box>
              </Box>

              {/* Loader */}
              {(productsLoading || isProductsRefreshing) && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={40} />
                </Box>
              )}

              {/* Products Table - Mobile Optimized */}
              {!productsLoading && !isProductsRefreshing && (
                <>
                  {paginatedProducts.length > 0 ? (
                    <>
                      <TableContainer 
                        sx={{ 
                          border: '1px solid #eee', 
                          borderRadius: 2, 
                          mb: 2,
                          maxHeight: { xs: '400px', sm: 'none' },
                          overflow: { xs: 'auto', sm: 'visible' }
                        }}
                      >
                        <Table size={isMobile ? "small" : "medium"}>
                          <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Product
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                                  display: { xs: 'none', sm: 'table-cell' }
                                }}
                              >
                                Category
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                                  display: { xs: 'none', sm: 'table-cell' }
                                }}
                              >
                                Seller
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                              >
                                Price
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                              >
                                Stock
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                              >
                                Status
                              </TableCell>
                              {!isMobile && (
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                  Actions
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedProducts.map((product) => (
                              <TableRow key={product._id} hover>
                                <TableCell>
                                  <Box>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight={600} 
                                      sx={{ 
                                        maxWidth: isMobile ? 150 : 200, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap',
                                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                                      }}
                                    >
                                      {product.name}
                                    </Typography>
                                    {!isMobile && (
                                      <Typography variant="caption" color="text.secondary">
                                        {product.brand || 'No brand'}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                {!isMobile && (
                                  <TableCell>
                                    <Chip
                                      label={product.category?.name || 'Uncategorized'}
                                      size="small"
                                      sx={{ 
                                        fontSize: '0.7rem', 
                                        bgcolor: '#f3f4f6', 
                                        border: '1px solid #e5e7eb' 
                                      }}
                                    />
                                  </TableCell>
                                )}
                                {!isMobile && (
                                  <TableCell>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        maxWidth: 120, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.875rem'
                                      }}
                                    >
                                      {product.seller?.brandName || 'Unknown'}
                                    </Typography>
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={600} 
                                    color="#ff2852"
                                    sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                                  >
                                    PKR {product.price?.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    color={product.stock > 0 ? 'success.main' : 'error.main'}
                                    sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                                  >
                                    {product.stock || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" gap={1} flexDirection="column">
                                    <Chip
                                      label={product.isActive ? 'Active' : 'Inactive'}
                                      size="small"
                                      sx={{
                                        fontSize: '0.7rem',
                                        bgcolor: product.isActive ? '#e8f5e9' : '#ffebee',
                                        color: product.isActive ? '#2e7d32' : '#d32f2f'
                                      }}
                                    />
                                    {product.verificationStatus && (
                                      <Chip
                                        label={product.verificationStatus}
                                        size="small"
                                        sx={{
                                          fontSize: '0.7rem',
                                          bgcolor: product.verificationStatus === 'approved' ? '#e3f2fd' : 
                                                    product.verificationStatus === 'pending' ? '#fff3e0' : '#ffebee',
                                          color: product.verificationStatus === 'approved' ? '#1976d2' : 
                                                         product.verificationStatus === 'pending' ? '#ed6c02' : '#d32f2f'
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                {!isMobile && (
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(`/products/${product._id}`, '_blank')}
                                      sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination */}
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredProducts.length}
                        rowsPerPage={productRowsPerPage}
                        page={productPage}
                        onPageChange={handleProductPageChange}
                        onRowsPerPageChange={handleProductRowsPerPageChange}
                        sx={{ border: '1px solid #eee', borderRadius: 2 }}
                      />
                    </>
                  ) : (
                    <Box textAlign="center" py={6}>
                      <Inventory sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No products found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {productSearch ? 'Try adjusting your search terms' : 
                         productFilter === 'active' ? 'No active products found' : 
                         'No products available'}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Quick Action Buttons */}
              <Box display="flex" gap={2} mt={3} pt={3} borderTop="1px solid #eee">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/products')}
                  sx={{ bgcolor: '#ff2852', '&:hover': { bgcolor: '#d63031' } }}
                >
                  Manage All Products
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/products/pending')}
                  sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                >
                  Pending Approval ({calculatedStats.pendingProducts})
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/categories')}
                  sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                >
                  Manage Categories
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Notifications Tab - Enhanced */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionHeader title="Notification Center" icon={<Notifications />} />
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="body1" color="text.secondary" mb={2}>
                Manage all system notifications, alerts, and user communications.
              </Typography>
              
              {/* Notification Stats */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ff285210', borderRadius: 2 }}>
                    <Notifications sx={{ fontSize: 32, color: '#ff2852', mb: 1 }} />
                    <Typography variant="h4" color="#ff2852" fontWeight={700}>
                      {notifications.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Notifications</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f59e0b15', borderRadius: 2 }}>
                    <Warning sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
                    <Typography variant="h4" color="#f59e0b" fontWeight={700}>
                      {notifications.filter(n => n.priority === 'high').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">High Priority</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10b98115', borderRadius: 2 }}>
                    <Info sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
                    <Typography variant="h4" color="#10b981" fontWeight={700}>
                      {notifications.filter(n => n.type === 'seller_verification').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Seller Requests</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#3b82f615', borderRadius: 2 }}>
                    <ShoppingCart sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                    <Typography variant="h4" color="#3b82f6" fontWeight={700}>
                      {notifications.filter(n => n.type === 'new_order').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">New Orders</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Quick Actions */}
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/notifications/all')}
                  sx={{ bgcolor: '#ff2852', '&:hover': { bgcolor: '#d63031' } }}
                >
                  View All Notifications
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/notifications/mark-all-read')}
                  sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                >
                  Mark All as Read
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/notifications/settings')}
                  sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                >
                  Notification Settings
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/notifications/clear')}
                  sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                >
                  Clear Old Notifications
                </Button>
              </Box>
              
              {/* Recent Notifications List */}
              <Typography variant="h6" fontWeight={700} mb={2}>Recent Notifications</Typography>
              {notifications.length > 0 ? (
                <List sx={{ bgcolor: '#fafafa', borderRadius: 2 }}>
                  {notifications.slice(0, 8).map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem 
                        sx={{ 
                          py: 2,
                          cursor: 'pointer',
                          transition: '0.2s',
                          '&:hover': { bgcolor: '#f8f9fa' }
                        }}
                        onClick={notification.action}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: `${notification.color}15`, color: notification.color }}>
                            {notification.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {notification.title}
                              </Typography>
                              <Chip
                                label={notification.priority}
                                size="small"
                                sx={{
                                  bgcolor: notification.priority === 'high' ? '#ffebee' : '#fff3e0',
                                  color: notification.priority === 'high' ? '#d32f2f' : '#ed6c02',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {notification.message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {notification.time}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box p={4} textAlign="center">
                  <Notifications sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1}>
                    No Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your notification center is currently empty. New notifications will appear here.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Delivery Tab - Enhanced */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionHeader title="Delivery Management" icon={<LocalShipping />} />
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="body1" color="text.secondary" mb={2}>
                Track and manage all delivery operations, orders, and logistics across the platform.
              </Typography>
              
              {/* Quick Actions */}
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/orders')}
                  sx={{ bgcolor: '#ff2852', '&:hover': { bgcolor: '#d63031' } }}
                >
                  All Orders
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/orders?status=out-for-delivery')}
                  sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                >
                  Out for Delivery
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/orders?status=delivered')}
                  sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                >
                  Delivered
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/orders/delivery-agents')}
                  sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                >
                  Delivery Agents
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/orders/delivery-areas')}
                  sx={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                >
                  Delivery Areas
                </Button>
              </Box>
              
              {/* Enhanced Delivery Stats */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e915', borderRadius: 2 }}>
                    <LocalShipping sx={{ fontSize: 32, color: '#2e7d32', mb: 1 }} />
                    <Typography variant="h4" color="#2e7d32" fontWeight={700}>
                      {orders?.filter(o => o.status === 'delivered' || o.orderStatus === 'delivered').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Delivered</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e015', borderRadius: 2 }}>
                    <LocalShipping sx={{ fontSize: 32, color: '#ed6c02', mb: 1 }} />
                    <Typography variant="h4" color="#ed6c02" fontWeight={700}>
                      {orders?.filter(o => o.status === 'out-for-delivery' || o.orderStatus === 'out-for-delivery').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Out for Delivery</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd15', borderRadius: 2 }}>
                    <Assignment sx={{ fontSize: 32, color: '#1976d2', mb: 1 }} />
                    <Typography variant="h4" color="#1976d2" fontWeight={700}>
                      {orders?.filter(o => o.status === 'processing' || o.orderStatus === 'processing').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Processing</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee15', borderRadius: 2 }}>
                    <Cancel sx={{ fontSize: 32, color: '#d32f2f', mb: 1 }} />
                    <Typography variant="h4" color="#d32f2f" fontWeight={700}>
                      {orders?.filter(o => o.status === 'cancelled' || o.orderStatus === 'cancelled').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Cancelled</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Delivery Performance Metrics */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={700} mb={2} color="#ff2852">
                      Delivery Performance
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box textAlign="center" p={1}>
                          <Typography variant="h5" color="#10b981" fontWeight={700}>
                            {orders?.filter(o => o.status === 'delivered').length ? 
                              Math.round((orders.filter(o => o.status === 'delivered').length / orders.length) * 100) : 0}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center" p={1}>
                          <Typography variant="h5" color="#f59e0b" fontWeight={700}>
                            {orders?.filter(o => o.status === 'delivered' && o.deliveryDate).length ? 
                              Math.round(orders.filter(o => o.status === 'delivered' && o.deliveryDate)
                                .reduce((acc, o) => acc + (o.deliveryDate ? new Date(o.deliveryDate).getTime() - new Date(o.createdAt).getTime() : 0), 0) / 
                                orders.filter(o => o.status === 'delivered' && o.deliveryDate).length / (1000 * 60 * 60 * 24)) : 0} days
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Avg Delivery Time</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={700} mb={2} color="#3b82f6">
                      Active Delivery Agents
                    </Typography>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="#3b82f6" fontWeight={700}>
                        {Math.floor(Math.random() * 20) + 10}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Agents Online</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Recent Deliveries */}
              <Typography variant="h6" fontWeight={700} mb={2}>Recent Orders</Typography>
              <Grid container spacing={2}>
                {orders?.slice(0, 6).map((order) => (
                  <Grid item xs={12} sm={6} md={4} key={order._id}>
                    <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #eee' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          #{order.orderNumber}
                        </Typography>
                        <Chip
                          label={order.status || order.orderStatus}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            textTransform: 'capitalize',
                            bgcolor: order.status === 'delivered' ? '#e8f5e9' : 
                                     order.status === 'out-for-delivery' ? '#fff3e0' : '#e3f2fd',
                            color: order.status === 'delivered' ? '#2e7d32' : 
                                   order.status === 'out-for-delivery' ? '#ed6c02' : '#1976d2'
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        {order.user?.name || 'Guest'}
                      </Typography>
                      <Typography variant="body2" color="#ff2852" fontWeight={700}>
                        PKR {order.totalAmount?.toLocaleString() || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
